import {
  SpineAsset,
  SpineVersion,
  SpineBone,
  SpineSlot,
  SpineSkin,
  SpineAnimation,
  SpineEvent,
} from '../../core/types/spine.types';

// ============================================================================
// SPINE JSON PARSER
// ============================================================================

interface SpineJsonSkeleton {
  hash?: string;
  spine?: string;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  fps?: number;
  images?: string;
  audio?: string;
}

interface SpineJsonBone {
  name: string;
  parent?: string;
  length?: number;
  rotation?: number;
  x?: number;
  y?: number;
  scaleX?: number;
  scaleY?: number;
  shearX?: number;
  shearY?: number;
  transform?: string;
  skin?: boolean;
  color?: string;
}

interface SpineJsonSlot {
  name: string;
  bone: string;
  color?: string;
  dark?: string;
  attachment?: string;
  blend?: string;
}

interface SpineJsonData {
  skeleton?: SpineJsonSkeleton;
  bones?: SpineJsonBone[];
  slots?: SpineJsonSlot[];
  skins?: Record<string, Record<string, unknown>> | Array<{ name: string; attachments: Record<string, unknown> }>;
  events?: Record<string, unknown>;
  animations?: Record<string, unknown>;
}

export function detectSpineVersion(jsonData: SpineJsonData): SpineVersion {
  const versionString = jsonData.skeleton?.spine || '';

  if (versionString.startsWith('4.2')) return '4.2';
  if (versionString.startsWith('4.1')) return '4.1';
  if (versionString.startsWith('4.0')) return '4.0';
  if (versionString.startsWith('3.8')) return '3.8';

  // Default to latest
  return '4.1';
}

export function parseSpineJson(jsonString: string): Partial<SpineAsset> {
  const data: SpineJsonData = JSON.parse(jsonString);

  const version = detectSpineVersion(data);

  // Parse bones
  const bones: SpineBone[] = (data.bones || []).map((bone) => ({
    name: bone.name,
    parentName: bone.parent || null,
    length: bone.length || 0,
    rotation: bone.rotation || 0,
    x: bone.x || 0,
    y: bone.y || 0,
    scaleX: bone.scaleX ?? 1,
    scaleY: bone.scaleY ?? 1,
  }));

  // Parse slots
  const slots: SpineSlot[] = (data.slots || []).map((slot) => ({
    name: slot.name,
    boneName: slot.bone,
    color: slot.color || 'ffffffff',
    attachment: slot.attachment || null,
    blendMode: (slot.blend as SpineSlot['blendMode']) || 'normal',
  }));

  // Parse skins
  const skins: SpineSkin[] = [];
  if (data.skins) {
    if (Array.isArray(data.skins)) {
      // Spine 4.x format
      for (const skin of data.skins) {
        skins.push({
          name: skin.name,
          attachments: {},
        });
      }
    } else {
      // Spine 3.x format
      for (const skinName of Object.keys(data.skins)) {
        skins.push({
          name: skinName,
          attachments: {},
        });
      }
    }
  }

  // Parse animations
  const animations: SpineAnimation[] = [];
  if (data.animations) {
    for (const [animName, animData] of Object.entries(data.animations)) {
      const anim = animData as Record<string, unknown>;
      animations.push({
        name: animName,
        duration: 0, // Would need to calculate from keyframes
        boneTimelines: anim.bones ? Object.keys(anim.bones as object) : [],
        slotTimelines: anim.slots ? Object.keys(anim.slots as object) : [],
        hasEvents: !!anim.events,
        eventNames: anim.events ? Object.keys(anim.events as object) : [],
      });
    }
  }

  // Parse events
  const events: SpineEvent[] = [];
  if (data.events) {
    for (const [eventName, eventData] of Object.entries(data.events)) {
      const evt = eventData as Record<string, unknown>;
      events.push({
        name: eventName,
        intValue: (evt.int as number) || 0,
        floatValue: (evt.float as number) || 0,
        stringValue: (evt.string as string) || '',
        audioPath: evt.audio as string | undefined,
        volume: evt.volume as number | undefined,
        balance: evt.balance as number | undefined,
      });
    }
  }

  return {
    version,
    width: data.skeleton?.width || 0,
    height: data.skeleton?.height || 0,
    bones,
    slots,
    skins,
    animations,
    events,
  };
}

// ============================================================================
// SPINE BINARY PARSER
// ============================================================================

/**
 * Parse a binary .skel file to extract metadata.
 *
 * This provides a lightweight metadata extraction without needing the full
 * spine runtime. For full skeleton data loading (with textures), use
 * SpineOffscreenRenderer.extractMetadata() which uses the official
 * @esotericsoftware/spine-pixi-v8 SkeletonBinary parser.
 *
 * This basic parser extracts the version string and skeleton dimensions
 * from the binary header for use during import/registration.
 */
export function parseSpineBinary(buffer: ArrayBuffer): Partial<SpineAsset> {
  try {
    const view = new DataView(buffer);
    const bytes = new Uint8Array(buffer);
    let offset = 0;

    // Spine binary format starts with a variable-length string (hash),
    // then another string (version), then width/height floats.
    // String format: length as varint, then UTF-8 bytes.

    // Read varint-length string
    const readString = (): string => {
      let length = readVarint();
      if (length === 0) return '';
      length--; // Spine subtracts 1 from stored length
      const str = new TextDecoder().decode(bytes.slice(offset, offset + length));
      offset += length;
      return str;
    };

    const readVarint = (): number => {
      let result = 0;
      let shift = 0;
      let b: number;
      do {
        b = bytes[offset++];
        result |= (b & 0x7f) << shift;
        shift += 7;
      } while (b & 0x80);
      return result;
    };

    const readFloat = (): number => {
      const val = view.getFloat32(offset, false); // big-endian
      offset += 4;
      return val;
    };

    // Header: hash string, version string
    const hash = readString();
    const versionStr = readString();

    // Detect version
    let version: SpineVersion = '4.1';
    if (versionStr.startsWith('4.2')) version = '4.2';
    else if (versionStr.startsWith('4.1')) version = '4.1';
    else if (versionStr.startsWith('4.0')) version = '4.0';
    else if (versionStr.startsWith('3.8')) version = '3.8';

    // Read dimensions
    const width = readFloat();
    const height = readFloat();

    return {
      version,
      width: width || 0,
      height: height || 0,
      bones: [],
      slots: [],
      skins: [{ name: 'default', attachments: {} }],
      animations: [],
      events: [],
    };
  } catch {
    // If parsing fails, return safe defaults
    return {
      version: '4.1',
      width: 0,
      height: 0,
      bones: [],
      slots: [],
      skins: [{ name: 'default', attachments: {} }],
      animations: [],
      events: [],
    };
  }
}

// ============================================================================
// ATLAS PARSER
// ============================================================================

export interface AtlasPage {
  name: string;
  format: string;
  filter: string;
  repeat: string;
  width: number;
  height: number;
}

export interface AtlasRegion {
  name: string;
  page: string;
  x: number;
  y: number;
  width: number;
  height: number;
  rotate: boolean;
  originalWidth: number;
  originalHeight: number;
  offsetX: number;
  offsetY: number;
  index: number;
}

export interface SpineAtlas {
  pages: AtlasPage[];
  regions: AtlasRegion[];
}

export function parseSpineAtlas(atlasText: string): SpineAtlas {
  const lines = atlasText.split('\n').map((line) => line.trim());
  const pages: AtlasPage[] = [];
  const regions: AtlasRegion[] = [];

  let currentPage: AtlasPage | null = null;
  let currentRegion: Partial<AtlasRegion> | null = null;
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line === '') {
      // Empty line - could be page separator or end
      if (currentRegion && currentRegion.name) {
        regions.push(currentRegion as AtlasRegion);
        currentRegion = null;
      }
      i++;
      continue;
    }

    // Check if this is a page header (no colon, file name)
    if (!line.includes(':') && (line.endsWith('.png') || line.endsWith('.jpg'))) {
      // New page
      if (currentRegion && currentRegion.name) {
        regions.push(currentRegion as AtlasRegion);
        currentRegion = null;
      }

      currentPage = {
        name: line,
        format: 'RGBA8888',
        filter: 'Linear,Linear',
        repeat: 'none',
        width: 0,
        height: 0,
      };
      pages.push(currentPage);
      i++;
      continue;
    }

    // Parse key: value pairs
    const colonIndex = line.indexOf(':');
    if (colonIndex !== -1) {
      const key = line.substring(0, colonIndex).trim();
      const value = line.substring(colonIndex + 1).trim();

      if (currentPage && !currentRegion) {
        // Page properties
        switch (key) {
          case 'size':
            const [w, h] = value.split(',').map((v) => parseInt(v.trim(), 10));
            currentPage.width = w;
            currentPage.height = h;
            break;
          case 'format':
            currentPage.format = value;
            break;
          case 'filter':
            currentPage.filter = value;
            break;
          case 'repeat':
            currentPage.repeat = value;
            break;
        }
      } else if (currentRegion) {
        // Region properties
        switch (key) {
          case 'xy':
            const [x, y] = value.split(',').map((v) => parseInt(v.trim(), 10));
            currentRegion.x = x;
            currentRegion.y = y;
            break;
          case 'size':
            const [rw, rh] = value.split(',').map((v) => parseInt(v.trim(), 10));
            currentRegion.width = rw;
            currentRegion.height = rh;
            break;
          case 'orig':
            const [ow, oh] = value.split(',').map((v) => parseInt(v.trim(), 10));
            currentRegion.originalWidth = ow;
            currentRegion.originalHeight = oh;
            break;
          case 'offset':
            const [ox, oy] = value.split(',').map((v) => parseInt(v.trim(), 10));
            currentRegion.offsetX = ox;
            currentRegion.offsetY = oy;
            break;
          case 'rotate':
            currentRegion.rotate = value === 'true' || value === '90';
            break;
          case 'index':
            currentRegion.index = parseInt(value, 10);
            break;
        }
      }
    } else if (currentPage && line && !line.includes(':')) {
      // This is a region name
      if (currentRegion && currentRegion.name) {
        regions.push(currentRegion as AtlasRegion);
      }

      currentRegion = {
        name: line,
        page: currentPage.name,
        x: 0,
        y: 0,
        width: 0,
        height: 0,
        rotate: false,
        originalWidth: 0,
        originalHeight: 0,
        offsetX: 0,
        offsetY: 0,
        index: -1,
      };
    }

    i++;
  }

  // Don't forget last region
  if (currentRegion && currentRegion.name) {
    regions.push(currentRegion as AtlasRegion);
  }

  return { pages, regions };
}

// ============================================================================
// FILE LOADING UTILITIES
// ============================================================================

export async function loadSpineFiles(
  skeletonFile: File,
  atlasFile: File,
  textureFiles: File[]
): Promise<{
  skeleton: Partial<SpineAsset>;
  atlas: SpineAtlas;
  textures: Map<string, string>; // filename -> dataURL
}> {
  // Load skeleton
  const skeletonText = await skeletonFile.text();
  let skeleton: Partial<SpineAsset>;

  if (skeletonFile.name.endsWith('.json')) {
    skeleton = parseSpineJson(skeletonText);
  } else {
    const buffer = await skeletonFile.arrayBuffer();
    skeleton = parseSpineBinary(buffer);
  }

  // Load atlas
  const atlasText = await atlasFile.text();
  const atlas = parseSpineAtlas(atlasText);

  // Load textures as data URLs
  const textures = new Map<string, string>();
  for (const textureFile of textureFiles) {
    const dataUrl = await fileToDataUrl(textureFile);
    textures.set(textureFile.name, dataUrl);
  }

  return { skeleton, atlas, textures };
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// ============================================================================
// VALIDATION UTILITIES
// ============================================================================

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  info: {
    version?: SpineVersion;
    boneCount: number;
    slotCount: number;
    skinCount: number;
    animationCount: number;
    eventCount: number;
  };
}

export async function validateSpineFiles(
  skeletonFile: File | null,
  atlasFile: File | null,
  textureFiles: File[]
): Promise<ValidationResult> {
  const errors: string[] = [];
  const warnings: string[] = [];
  const info = {
    version: undefined as SpineVersion | undefined,
    boneCount: 0,
    slotCount: 0,
    skinCount: 0,
    animationCount: 0,
    eventCount: 0,
  };

  if (!skeletonFile) {
    errors.push('Missing skeleton file (.skel or .json)');
  }

  if (!atlasFile) {
    errors.push('Missing atlas file (.atlas)');
  }

  if (textureFiles.length === 0) {
    errors.push('Missing texture file(s) (.png)');
  }

  // Early return if missing required files
  if (errors.length > 0) {
    return { isValid: false, errors, warnings, info };
  }

  try {
    // Parse skeleton
    const skeletonText = await skeletonFile!.text();
    let skeleton: Partial<SpineAsset>;

    if (skeletonFile!.name.endsWith('.json')) {
      skeleton = parseSpineJson(skeletonText);
    } else {
      const buffer = await skeletonFile!.arrayBuffer();
      skeleton = parseSpineBinary(buffer);
    }

    info.version = skeleton.version;
    info.boneCount = skeleton.bones?.length || 0;
    info.slotCount = skeleton.slots?.length || 0;
    info.skinCount = skeleton.skins?.length || 0;
    info.animationCount = skeleton.animations?.length || 0;
    info.eventCount = skeleton.events?.length || 0;

    // Parse atlas to check texture references
    const atlasText = await atlasFile!.text();
    const atlas = parseSpineAtlas(atlasText);

    // Check that all atlas pages have corresponding textures
    const textureNames = new Set(textureFiles.map((f) => f.name));
    for (const page of atlas.pages) {
      if (!textureNames.has(page.name)) {
        errors.push(`Missing texture file: ${page.name}`);
      }
    }

    // Warnings
    if (info.skinCount === 0) {
      warnings.push('No skins defined - using default skin');
    }

    if (info.animationCount === 0) {
      warnings.push('No animations defined');
    }

  } catch (error) {
    errors.push(`Failed to parse files: ${error}`);
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    info,
  };
}
